import { useLayoutEffect, useRef, type RefObject } from "react";
import { easeOut, progress, type DemoScene } from "./demoScenario";

type RevealStep = readonly [selector: string, delay: number, stagger?: number];
// Reading order is explicit: shells settle before their rows, independent of DOM nesting.
const SEQUENCES: Partial<Record<DemoScene["screen"], readonly RevealStep[]>> = {
  "menu-result": [
    [".nd-menu-best", 0], [".nd-menu-context", 100], [".nd-menu-recommendation", 220],
    [".nd-menu-alternative", 450], [".nd-menu-customize > *", 150, 180],
  ],
  "nutrient-status": [
    [".nd-native-header h3", 0], [".nd-filter-panel", 100],
    [".nd-nutrient-card", 250, 180],
  ],
  "fridge-result": [
    [".nd-native-header h3", 0], [".nd-fridge-result-photo", 100],
    [".nd-fridge-intro h4", 280], [".nd-fridge-intro p", 410],
    [".nd-fridge-recommendation", 650], [".nd-fridge-recommendation > *", 780, 120],
  ],
  overview: [
    [".nd-overview-heading", 0], [".nd-period", 90],
    [".nd-overview-cards > .nd-card", 190, 330],
    [".nd-overview-cards > .nd-card:first-child .nd-status-heading", 260],
    [".nd-overview-cards > .nd-card:first-child .nd-divider", 340],
    [".nd-overview-cards > .nd-card:first-child .nd-suggestion", 410, 110],
    [".nd-overview-cards > .nd-card:last-child .nd-suggestion", 980, 110],
  ],
  log: [
    [".nd-log-heading", 0], [".nd-date-picker", 90], [".nd-meal-timeline", 180],
    [".nd-meal-timeline-line > div", 270, 95], [".nd-log-entry", 470, 210],
    [".nd-log-entry .nd-impact", 630, 210],
  ],
  detail: [
    [".nd-editor-top", 0], [".nd-editor > h3", 80], [".nd-editor-tabs", 170],
    [".nd-editor > .nd-secondary-action", 250], 
    [".nd-editor-food", 410], [".nd-editor-row", 510, 110], [".nd-bottom-action", 1000],
  ],
  review: [
    [".nd-native-header h3", 0], [".nd-review-photo", 100], [".nd-meal-name", 220],
    [".nd-food-card", 300], [".nd-ingredients > div", 400, 100],
    [".nd-improve-heading", 850], [".nd-improvement", 940], [".nd-bottom-action", 1100],
  ],
  recommendation: [
    [".nd-native-header h3", 0], [".nd-filter-panel", 100],
    [".nd-recommendation-card", 270, 650],
    [".nd-food-option", 430, 110], [".nd-inline-prompt", 1400],
  ],
  prefill: [
    [".nd-native-header h3", 0], [".nd-voice-guide", 100],
    [".nd-voice-guide > div", 230, 140], [".nd-voice-canvas", 900],
    [".nd-voice-input", 1080], [".nd-mic", 1210], [".nd-bottom-action", 1320],
  ],
};

export function useContentReveal(ref: RefObject<HTMLDivElement>, scene: DemoScene, time: number, still: boolean) {
  const items = useRef<{ element: HTMLElement; delay: number; previous: number }[]>([]);
  useLayoutEffect(() => {
    const root = ref.current;
    const sequence = scene.screen === "menu-result" && scene.complete
      ? SEQUENCES[scene.screen]?.filter(([selector]) => selector === ".nd-menu-customize > *")
      : scene.screen === "nutrient-status" && scene.start > 0
      ? SEQUENCES[scene.screen]?.filter(([selector]) => selector === ".nd-nutrient-card")
      : SEQUENCES[scene.screen];
    if (!root || !sequence) { items.current = []; return; }
    const elements = new Map<HTMLElement, number>();
    for (const [selector, delay, stagger = 0] of sequence) {
      root.querySelectorAll<HTMLElement>(selector).forEach((element, index) => elements.set(element, delay + index * stagger));
    }
    items.current = Array.from(elements, ([element, delay]) => ({ element, delay, previous: -1 }));
    for (const { element } of items.current) element.classList.add("nd-reveal-item");
    return () => {
      for (const { element } of items.current) {
        element.classList.remove("nd-reveal-item");
        for (const key of ["opacity", "y"]) element.style.removeProperty(`--reveal-${key}`);
      }
      items.current = [];
    };
  }, [ref, scene.id, scene.screen, scene.start, scene.complete]);

  useLayoutEffect(() => {
    for (const item of items.current) {
      const value = still ? 1 : easeOut(progress(time, scene.start + item.delay, 520));
      if (value === item.previous) continue;
      item.previous = value;
      item.element.style.setProperty("--reveal-opacity", `${value}`);
      item.element.style.setProperty("--reveal-y", `${-7 * (1 - value)}px`);
    }
  }, [scene.id, scene.start, time, still]);
}
